import {
    Button,
    Checkbox,
    DatePicker,
    Form,
    Image,
    Input,
    InputNumber,
    message,
    Modal,
    Select,
    Space
} from "antd";
import type {
    GoodsShip,
    ProductVo,
    Project,
    PurchaseGoods,
    PurchaseGoodsForm,
    PurchaseGoodsVo,
    Ship,
    TransportOrderShipVo
} from "@zhihaoscm/entity";
import {
    PERMISSION,
    PRODUCT_PRICE_TYPE,
    PRODUCT_STATE,
    PROJECT_STATE,
    PURCHASE_GOODS_TYPE,
    PURCHASE_PAYMENT_TYPE,
    ROLE_CODE,
    TRANSPORT_TYPE
} from "@zhihaoscm/meta";
import { dateFormat, dateTimeFormat, formatNumber } from "@zhihaoscm/utils";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { productService } from "@/api/productService";
import { projectService } from "@/api/projectService";
import { purchaseGoodsService } from "@/api/purchaseGoodsService";
import { shipService } from "@/api/shipService";
import { transportOrderShipService } from "@/api/transportOrderShipService";
import {
    Breadcrumb,
    DetailButtons,
    DetailHeader,
    InfoCard,
    ProTable,
    ZhBaseItem,
    ZhGrid,
    ZhInsetTabs,
    ZhMessageBox,
    type DetailAction,
    type ProColumn
} from "@/components/common";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import {
    DOWN_STREAM_PURCHASE_GOODS_STATE,
    PURCHASE_GOODS_STATE,
    RECEIPT_TYPE
} from "../../../../../common/enum/purchaseGoods";
import { FORWARD } from "../../../../../common/enum/purchaseGoodsReceipt";
import "./console.css";

const metaOptions = (meta: { options: Array<{ label: string; value: string | number }> }) =>
    meta.options.map(item => ({ label: item.label, value: Number(item.value) }));

type PurchaseGoodsActive = "My" | "Down";
/** 提货列表逐项对应旧 index.vue 的角色页签、筛选、状态展示和操作门禁。 */
export function PurchaseGoodsListPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const userInfo = useUserSession(state => state.userInfo);
    const account = useAccountSession();
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const [active, setActive] = useState<PurchaseGoodsActive>(() => {
        const purGoodsType = searchParams.get("purGoodsType");
        return purGoodsType === "My" || purGoodsType === "Down" ? purGoodsType : isSupplier ? "Down" : "My";
    });
    const [refreshKey, setRefreshKey] = useState(0);
    const [confirmState, setConfirmState] = useState<{
        title: string;
        content: string;
        completed?: string;
        action: () => Promise<unknown>;
    }>();
    const isMy = active === "My";
    const type = isMy ? PURCHASE_GOODS_TYPE.ONE.code : PURCHASE_GOODS_TYPE.TWO.code;
    const title = isCustomer ? "我的提货单" : isSupplier ? "客户提货单" : undefined;
    const states = metaOptions(isMy ? PURCHASE_GOODS_STATE : DOWN_STREAM_PURCHASE_GOODS_STATE);
    const receipts = metaOptions(RECEIPT_TYPE);
    const forwards = metaOptions(FORWARD);
    const permissions = (userInfo as any).permissions as number[] | undefined;
    const hasPermission =
        account.accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.FREIGHT.code));
    const reload = () => setRefreshKey(value => value + 1);
    /** 签署接口存在票据时打开签署页，否则沿用旧版等待专人签署提示。 */
    const openTicket = async (action: () => Promise<any>) => {
        const data = await action();
        if (data?.errors || data?.status === 400) return;
        if (!data?.ticket) {
            void message.success("已发起签署，请等待专人进行签署");
            return;
        }
        window.open(data.ticket);
        reload();
    };
    /** 旧 confirmWrapper 使用 zhMessageBox，不引入 Ant Design 默认确认图标和按钮文案。 */
    const confirm = (content: string, action: () => Promise<unknown>, completed?: string, modalTitle = "提示") => {
        setConfirmState({ title: modalTitle, content, completed, action });
    };
    /** 旧 getOperationButtons(My) 的显示条件原样映射。 */
    const operation = (row: PurchaseGoods) => {
        const draft = row.state === PURCHASE_GOODS_STATE.DRAFT.code;
        const sealUser = String(account.actualAccount?.id) === String((row.receiverEnterprise as any)?.signId);
        if (draft && hasPermission)
            return (
                <Space className="purchase-goods-operation">
                    {isCustomer ? (
                        <Button type="link" onClick={() => navigate(`/console/purchaseGoods/add?id=${row.id}`)}>
                            修改
                        </Button>
                    ) : null}
                    <Button type="link" onClick={() => void openTicket(() => purchaseGoodsService.startSign(row.id))}>
                        发起签署
                    </Button>
                    <Button
                        danger
                        type="link"
                        onClick={() =>
                            confirm("是否删除，删除后您可以重新发起提货", () => purchaseGoodsService.delete(row.id), "已删除")
                        }
                    >
                        删除
                    </Button>
                </Space>
            );
        if (row.state === PURCHASE_GOODS_STATE.SIGNING.code && sealUser)
            return (
                <Button type="link" onClick={() => void openTicket(() => purchaseGoodsService.sign(row.id))}>
                    签署
                </Button>
            );
        if (
            row.state === PURCHASE_GOODS_STATE.FINISHED.code &&
            row.receiptState === RECEIPT_TYPE.UNSIGNED.code &&
            row.forward === FORWARD.UNFORWARD.code &&
            hasPermission
        )
            return (
                <Button
                    type="link"
                    onClick={() =>
                        confirm(
                            "作废后，需签署作废提货单后，该提货单将作废，是否发起作废？",
                            () => openTicket(() => purchaseGoodsService.startInvalid(Number(row.id))),
                            "已发起作废"
                        )
                    }
                >
                    发起作废
                </Button>
            );
        if (row.state === PURCHASE_GOODS_STATE.INVALIDING.code && sealUser)
            return (
                <Button type="link" onClick={() => void openTicket(() => purchaseGoodsService.sign(row.id))}>
                    签署作废文件
                </Button>
            );
        return "--";
    };
    const columns = useMemo<ProColumn<PurchaseGoods>[]>(
        () => [
            {
                key: "id",
                title: "编号",
                width: 150,
                search: { key: "keyword", type: "input", placeholder: "请输入编号或项目名称" },
                render: (_, row) => (
                    <Button
                        className="purchase-goods-code-link"
                        type="link"
                        onClick={() => navigate(`/console/purchaseGoods/detail?id=${row.id}&purGoodsType=${active}`)}
                    >
                        {row.id}
                    </Button>
                )
            },
            { key: "projectName", title: "项目", minWidth: 170 },
            {
                key: "productTypeName",
                title: "品类",
                minWidth: 170,
                render: (_, row) =>
                    [row.productTypeName, (row as any).area, (row as any).productTypeId].filter(Boolean).join(" | ") ||
                    "--"
            },
            {
                key: "shipName",
                title: "船名",
                minWidth: 170,
                render: (_, row) =>
                    row.ships
                        ?.map(ship => [ship.cnName || (ship as any).cnname, ship.name].filter(Boolean).join("/"))
                        .join("、") || "--"
            },
            ...(isMy
                ? [
                      {
                          key: "supplierEnterprise",
                          title: "供货单位",
                          minWidth: 114,
                          search: { key: "enterprise", type: "input" as const, placeholder: "请输入供货单位名" },
                          render: (_: unknown, row: PurchaseGoods) => row.supplierEnterprise?.name || "--"
                      }
                  ]
                : [
                      {
                          key: "receiverEnterprise",
                          title: "提货单位",
                          minWidth: 114,
                          search: { key: "enterprise", type: "input" as const, placeholder: "请输入提货单位名" },
                          render: (_: unknown, row: PurchaseGoods) =>
                              row.receiverEnterprise?.name ||
                              (row.receiverEnterprise as any)?.legalRepresentative ||
                              (row.receiverEnterprise as any)?.realName ||
                              "--"
                      }
                  ]),
            { key: "quantity", title: "吨数", minWidth: 114, render: (_, row) => formatNumber(row.quantity) },
            {
                key: "applyDate",
                title: "申请日期",
                minWidth: 114,
                search: { key: "applyDate", type: "dateRange", placeholder: "申请日期范围选择", width: 80 },
                render: (_, row) => dateFormat(row.applyDate)
            },
            {
                key: "state",
                title: "提货状态",
                minWidth: 86,
                enum: states,
                search: { key: "state", type: "select", placeholder: "请选择提货状态", options: states, span: 3 }
            },
            {
                key: "receiptState",
                title: "签收状态",
                minWidth: 86,
                enum: receipts,
                search: { key: "receiptState", type: "select", placeholder: "请选择签收状态", options: receipts, span: 3 },
                render: (_, row) =>
                    row.state === PURCHASE_GOODS_STATE.FINISHED.code
                        ? RECEIPT_TYPE.getIntroByCode(row.receiptState)
                        : "--"
            },
            ...(!isMy && isSupplyChain
                ? [
                      {
                          key: "forward",
                          title: "是否已转发",
                          minWidth: 100,
                          enum: forwards,
                          search: {
                              key: "forward",
                              type: "select" as const,
                              placeholder: "请选择是否已转发",
                              options: forwards,
                              span: 3
                          }
                      }
                  ]
                : []),
            ...(!isSupplier && isMy
                ? [
                      {
                          key: "operation",
                          title: "操作",
                          width: 240,
                          fixed: "right" as const,
                          render: (_: unknown, row: PurchaseGoods) => operation(row)
                      }
                  ]
                : [])
        ],
        [active, forwards, hasPermission, isCustomer, isMy, isSupplier, isSupplyChain, navigate, receipts, states]
    );
    return (
        <section className="purchase-goods-list-page section-card">
            <Breadcrumb routes={[{ name: "采购" }, { name: title || "提货单" }]} />
            {isSupplyChain ? (
                <ZhInsetTabs
                    active={active}
                    onChange={setActive}
                    options={[
                        { label: "我的提货单", value: "My" },
                        { label: "下游提货单", value: "Down" }
                    ]}
                />
            ) : null}
            <div className="purchase-goods-table">
                <ProTable
                    key={`${active}-${refreshKey}`}
                    title={title}
                    rowKey="id"
                    columns={columns}
                    initialParams={{ type }}
                    request={params => purchaseGoodsService.paging({ ...params, type })}
                    header={
                        isMy && hasPermission
                            ? () => (
                                  <Button type="primary" onClick={() => navigate("/console/purchaseGoods/add")}>
                                      新增
                                  </Button>
                              )
                            : undefined
                    }
                />
            </div>
            <ZhMessageBox
                open={Boolean(confirmState)}
                title={confirmState?.title}
                tip={confirmState?.content}
                onCancel={() => setConfirmState(undefined)}
                onSubmit={async () => {
                    if (!confirmState) return;
                    await confirmState.action();
                    if (confirmState.completed) void message.success(confirmState.completed);
                    setConfirmState(undefined);
                    reload();
                }}
            />
        </section>
    );
}

interface GoodsFormValues extends Omit<PurchaseGoodsForm, "applyDate" | "expectedDeliveryDate"> {
    applyDate: dayjs.Dayjs;
    expectedDeliveryDate?: dayjs.Dayjs;
}
/** 旧 PurchaseGoodsShipCard 的 React 对应物，船舶字段仍逐卡编辑。 */
function GoodsShipCard({
    ship,
    onChange,
    onClose
}: {
    ship: GoodsShip;
    onChange: (ship: GoodsShip) => void;
    onClose: () => void;
}) {
    const name = [ship.cnName || (ship as any).cnname, ship.name].filter(Boolean).join("/");
    return (
        <div className="purchase-goods-ship-card">
            <header>
                <strong>{name || "--"}</strong>
                <button className="iconfont icon-guanbi" aria-label="移除船舶" type="button" onClick={onClose} />
            </header>
            <div>
                <label>船运单</label>
                <span>{ship.orderShipId || "--"}</span>
            </div>
            <div>
                <label>
                    <i>*</i>提货吨数
                </label>
                <InputNumber
                    min={1}
                    precision={0}
                    value={ship.ton}
                    placeholder="请输入运货吨数"
                    onChange={value => onChange({ ...ship, ton: Number(value || 0) })}
                />
            </div>
            <div>
                <label>手机号</label>
                <Input
                    value={ship.captinMobile}
                    placeholder="请输入手机号码"
                    onChange={event => onChange({ ...ship, captinMobile: event.target.value })}
                />
            </div>
            <div>
                <label>备注</label>
                <Input.TextArea
                    rows={2}
                    maxLength={20}
                    showCount
                    value={ship.remark}
                    placeholder="请输入备注"
                    onChange={event => onChange({ ...ship, remark: event.target.value })}
                />
            </div>
        </div>
    );
}

/** 新增/修改提货单映射旧四列表单、项目摘要、关联船舶与下游提货单。 */
export function PurchaseGoodsFormPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const forward = params.get("forward");
    const initialProjectId = params.get("projectId");
    const [form] = Form.useForm<GoodsFormValues>();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const receiverCustomerId = useAccountSession(state => state.proxyActualAccount.id);
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectInfo, setProjectInfo] = useState<any>();
    const [remainQuantity, setRemainQuantity] = useState(0);
    const [ships, setShips] = useState<GoodsShip[]>([]);
    const [draftShips, setDraftShips] = useState<GoodsShip[]>([]);
    const [shipDialog, setShipDialog] = useState(false);
    const [shipTab, setShipTab] = useState<"order" | "ship">("order");
    const [shipKeyword, setShipKeyword] = useState("");
    const [shipOptions, setShipOptions] = useState<Array<Ship | TransportOrderShipVo>>([]);
    const [downstreamDialog, setDownstreamDialog] = useState(false);
    const [downstreamOptions, setDownstreamOptions] = useState<PurchaseGoods[]>([]);
    const [downstream, setDownstream] = useState<PurchaseGoods[]>([]);
    const [draftDownstream, setDraftDownstream] = useState<PurchaseGoods[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    useEffect(() => {
        void projectService.noPaging({ states: String(PROJECT_STATE.PROCESSING.code) } as never).then(setProjects);
    }, []);
    useEffect(() => {
        if (!id) return;
        void purchaseGoodsService
            .getInfo(id)
            .then(async data => {
                const { purchaseGoods } = data;
                const [projectData, remain] = await Promise.all([
                    projectService.getInfo(purchaseGoods.projectId),
                    projectService.getQuantity({ projectId: purchaseGoods.projectId })
                ]);
                setProjectInfo(projectData);
                setProjects(value => value.some(item => item.id === projectData.project.id) ? value : [...value, projectData.project]);
                setRemainQuantity(remain + (!forward ? purchaseGoods.quantity : 0));
                setShips(purchaseGoods.ships);
                form.setFieldsValue({
                    ...purchaseGoods,
                    applyDate: dayjs(purchaseGoods.applyDate),
                    expectedDeliveryDate: purchaseGoods.expectedDeliveryDate
                        ? dayjs(purchaseGoods.expectedDeliveryDate)
                        : undefined
                });
            });
    }, [form, forward, id]);
    /** 项目切换后读取旧 ProjectVoManager 所需详情和剩余可提吨数。 */
    const selectProject = async (projectId: number) => {
        const [data, remain] = await Promise.all([
            projectService.getInfo(projectId),
            projectService.getQuantity({ projectId })
        ]);
        setProjectInfo(data);
        setRemainQuantity(remain);
        setDownstream([]);
        setShips([]);
        form.setFieldsValue({ quantity: 0, relatedGoodsIds: [] });
    };
    useEffect(() => {
        if (!initialProjectId || id) return;
        form.setFieldValue("projectId", Number(initialProjectId));
        void selectProject(Number(initialProjectId));
    }, [form, id, initialProjectId]);
    useEffect(() => {
        if (!shipDialog) return;
        const load = async () =>
            setShipOptions(
                shipTab === "order"
                    ? await transportOrderShipService.voSelector({ keyword: shipKeyword, isGoodsId: false })
                    : shipKeyword ? (await shipService.selector({ keyword: shipKeyword })).list : []
            );
        void load();
    }, [shipDialog, shipKeyword, shipTab]);
    const addShipOption = (option: Ship | TransportOrderShipVo) => {
        const vo = option as TransportOrderShipVo;
        const ship = vo.shipVo?.ship || (option as Ship);
        const order = vo.transportOrderShip;
        const next = {
            id: String(ship.id),
            name: ship.name,
            cnName: ship.cnname,
            captinMobile: ship.mobile,
            orderShipId: order?.id,
            ton: 0
        } as GoodsShip;
        setDraftShips(value => value.some(item => item.id === next.id && item.orderShipId === next.orderShipId)
            ? value.filter(item => !(item.id === next.id && item.orderShipId === next.orderShipId))
            : [...value, next]);
    };
    const openShipDialog = () => { setDraftShips(ships.map(item => ({ ...item }))); setShipDialog(true); };
    const openDownstream = async () => {
        const projectId = form.getFieldValue("projectId");
        if (!projectId) {
            void message.info("请先关联项目");
            return;
        }
        setDownstreamOptions(await purchaseGoodsService.getDownStreamList(String(projectId)));
        setDraftDownstream(downstream.map(item => ({ ...item })));
        setDownstreamDialog(true);
    };
    useEffect(() => {
        const quantity = ships.reduce((total, ship) => total + Number(ship.ton || 0), 0);
        form.setFieldValue("quantity", quantity);
    }, [form, ships]);
    const submit = (values: GoodsFormValues) => {
        if (isCustomer && !ships.length) {
            void message.error("请关联船舶");
            return;
        }
        if (Number(values.quantity) > remainQuantity) {
            void message.error(`不可超过预计最大提货吨数: ${remainQuantity}`);
            return;
        }
        const invalidShip = ships.find(ship => !Number.isInteger(Number(ship.ton)) || Number(ship.ton) <= 0);
        if (invalidShip) { void message.error(invalidShip.ton ? "运货吨数只能为正整数" : "请输入运货吨数"); return; }
        const invalidMobile = ships.find(ship => ship.captinMobile && !/^1\d{10}$/.test(String(ship.captinMobile)));
        if (invalidMobile) { void message.error("请输入正确的手机号"); return; }
        setSaveConfirmOpen(true);
    };
    /** 旧 confirmWrapper 在确认后才执行新增/修改，并返回提货单列表。 */
    const saveDraft = async () => {
        const values = await form.validateFields();
        setSubmitting(true);
        const payload = {
            ...values,
            applyDate: values.applyDate.format("YYYY-MM-DD"),
            expectedDeliveryDate: values.expectedDeliveryDate?.format("YYYY-MM-DD"),
            transportType: TRANSPORT_TYPE.SHIP.code,
            type: values.type ?? (isCustomer
                ? projectInfo?.distinguishData?.paymentType || PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code
                : PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code),
            receiverCustomerId,
            ships,
            relatedGoodsIds: downstream.map(item => item.id)
        } as PurchaseGoodsForm;
        try {
            if (id && !forward) await purchaseGoodsService.update(id, payload);
            else await purchaseGoodsService.create(payload);
            setSaveConfirmOpen(false);
            navigate("/console/purchaseGoods");
        } finally { setSubmitting(false); }
    };
    return (
        <section className="purchase-goods-form-page">
            <div className="purchase-goods-form-card common-card">
                <Breadcrumb
                    routes={[
                        { name: "采购" },
                        { name: "提货单", path: "/console/purchaseGoods" },
                        { name: forward ? "转发提货单" : id ? "修改提货单" : "新增提货单" }
                    ]}
                />
                <h1 className="common-title">{forward ? "转发提货单" : id ? "修改提货单" : "新增提货单"}</h1>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ transportType: TRANSPORT_TYPE.SHIP.code, receiverCustomerId, relatedGoodsIds: [] }}
                    onFinish={submit}
                >
                    <div className="purchase-goods-form-grid">
                        <div className="purchase-goods-project-field">
                            <Form.Item
                                name="projectId"
                                label="关联项目"
                                rules={[{ required: true, message: "请选择需要关联的项目" }]}
                            >
                                <Select
                                    showSearch
                                    filterOption={false}
                                    disabled={Boolean(id)}
                                    placeholder="请下拉选择或搜索项目"
                                    options={projects.map(item => ({ label: item.name, value: item.id }))}
                                    onSearch={value =>
                                        void projectService
                                            .noPaging({
                                                states: String(PROJECT_STATE.PROCESSING.code),
                                                name: value
                                            } as never)
                                            .then(setProjects)
                                    }
                                    onChange={selectProject}
                                />
                            </Form.Item>
                            {projectInfo ? (
                                <div className="purchase-goods-project-summary">
                                    <span>
                                        提货单位
                                        <strong>
                                            {projectInfo.distinguishData?.payer?.name ||
                                                projectInfo.purchaseGoods?.receiverEnterprise?.name ||
                                                "--"}
                                        </strong>
                                    </span>
                                    <span>
                                        供货单位
                                        <strong>
                                            {projectInfo.distinguishData?.acceptor?.name ||
                                                projectInfo.project?.supplierEnterprise?.name ||
                                                "--"}
                                        </strong>
                                    </span>
                                    <span>
                                        品类
                                        <strong>
                                            {[
                                                projectInfo.project?.productTypeName,
                                                projectInfo.project?.area,
                                                projectInfo.project?.productTypeId
                                            ]
                                                .filter(Boolean)
                                                .join(" | ") || "--"}
                                        </strong>
                                    </span>
                                    <span>
                                        预估单价
                                        <strong>
                                            {formatNumber(
                                                projectInfo.detailInfo?.unitPrice ||
                                                    projectInfo.project?.estimatedUnitPrice
                                            )}{" "}
                                            元/吨
                                        </strong>
                                    </span>
                                </div>
                            ) : null}
                        </div>
                        <Form.Item name="quantity" label="吨数">
                            <InputNumber disabled min={0} max={remainQuantity} style={{ width: "100%" }} />
                        </Form.Item>
                        {isCustomer && projectInfo ? (
                            <div className="purchase-goods-remain">预计剩余可提 {formatNumber(remainQuantity)}吨</div>
                        ) : (
                            <div />
                        )}
                        <div className="purchase-goods-associate">
                            {" "}
                            <label>{isCustomer ? "关联船舶" : "关联下游提货单"}</label>
                            <Button
                                type="link"
                                onClick={() => (isCustomer ? openShipDialog() : void openDownstream())}
                            >
                                {(isCustomer ? ships.length : downstream.length) ? "重新关联" : "关联"}
                                {!isCustomer ? <i className="iconfont icon-Frame-3" /> : null}
                            </Button>
                        </div>
                        <div className="purchase-goods-associated-cards">
                            {isCustomer
                                ? ships.map((ship, index) => (
                                      <GoodsShipCard
                                          key={`${ship.id}-${ship.orderShipId || index}`}
                                          ship={ship}
                                          onChange={next =>
                                              setShips(items =>
                                                  items.map((item, itemIndex) => (itemIndex === index ? next : item))
                                              )
                                          }
                                          onClose={() =>
                                              setShips(items => items.filter((_, itemIndex) => itemIndex !== index))
                                          }
                                      />
                                  ))
                                : downstream.map(item => (
                                      <div className="purchase-goods-downstream-card" key={item.id}>
                                          <header>
                                              {item.id}
                                              <button
                                                  className="iconfont icon-guanbi"
                                                  aria-label="移除提货单"
                                                  type="button"
                                                  onClick={() =>
                                                      setDownstream(items =>
                                                          items.filter(current => current.id !== item.id)
                                                      )
                                                  }
                                              />
                                          </header>
                                          {item.ships.map(ship => (
                                              <div key={`${item.id}-${ship.id}`}>
                                                  <span>提货单位 {item.receiverEnterprise?.name || "--"}</span>
                                                  <span>船名 {[ship.cnName, ship.name].filter(Boolean).join("/")}</span>
                                                  <span>运货吨数 {ship.ton}</span>
                                              </div>
                                          ))}
                                      </div>
                                  ))}
                        </div>
                        <Form.Item
                            name="applyDate"
                            label="提货申请日期"
                            rules={[{ required: true, message: "请选择提货申请日期" }]}
                        >
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="expectedDeliveryDate" label="预计提货日期">
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="remark" label="备注">
                            <Input.TextArea maxLength={200} showCount placeholder="如有其它说明, 请备注" />
                        </Form.Item>
                    </div>
                </Form>
            </div>
            <div className="detail-buttons purchase-goods-form-actions">
                <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                    保存为草稿
                </Button>
                <Button type="primary" onClick={() => navigate("/console/purchaseGoods")}>返回</Button>
            </div>
            <Modal
                open={shipDialog}
                width={582}
                title={null}
                onCancel={() => setShipDialog(false)}
                onOk={() => { setShips(draftShips); setShipDialog(false); }}
            >
                <ZhInsetTabs
                    active={shipTab}
                    onChange={setShipTab}
                    options={[
                        { label: "船运单船舶", value: "order" },
                        { label: "所有船舶", value: "ship" }
                    ]}
                />
                <Input
                    value={shipKeyword}
                    placeholder={shipTab === "order" ? "请输入船舶或船运单编号" : "请输入船舶或MMSI号"}
                    onChange={event => setShipKeyword(event.target.value)}
                />
                <div className="purchase-goods-select-list">
                    {shipOptions.map((option, index) => {
                        const vo = option as TransportOrderShipVo;
                        const ship = vo.shipVo?.ship || (option as Ship);
                        const order = vo.transportOrderShip;
                        return (
                            <Checkbox checked={draftShips.some(item => item.id === String(ship.id) && item.orderShipId === order?.id)} key={order?.id || ship.id || index} onChange={() => addShipOption(option)}>
                                <strong>
                                    {order
                                        ? `${order.sourcePortName} → ${order.destinationPortName}`
                                        : [ship.cnname, ship.name].filter(Boolean).join("/")}
                                </strong>
                                <span>
                                    {order
                                        ? `编号 ${order.id}　船舶 ${[ship.cnname, ship.name].filter(Boolean).join("/")}`
                                        : `MMSI ${ship.id}`}
                                </span>
                            </Checkbox>
                        );
                    })}
                </div>
                {draftShips.length ? <div className="purchase-goods-selected-ships"><strong>已选择的船舶 (<em>{new Set(draftShips.map(item => item.id)).size}</em>)</strong><div>{Array.from(new Set(draftShips.map(item => [item.cnName, item.name].filter(Boolean).join("/")))).map(name => <button type="button" key={name} onClick={() => setDraftShips(items => items.filter(item => [item.cnName, item.name].filter(Boolean).join("/") !== name))}>{name}<i className="iconfont icon-guanbi" /></button>)}</div></div> : null}
            </Modal>
            <Modal
                open={downstreamDialog}
                width={582}
                title="关联下游提货单"
                onCancel={() => setDownstreamDialog(false)}
                onOk={() => { setDownstream(draftDownstream); setDownstreamDialog(false); }}
            >
                <Checkbox.Group
                    value={draftDownstream.map(item => item.id)}
                    onChange={ids => setDraftDownstream(downstreamOptions.filter(item => ids.includes(item.id)))}
                >
                    <div className="purchase-goods-select-list">
                        {downstreamOptions.map(item => (
                            <Checkbox key={item.id} value={item.id}>
                                {item.id}
                            </Checkbox>
                        ))}
                    </div>
                </Checkbox.Group>
            </Modal>
            <ZhMessageBox
                open={saveConfirmOpen}
                tip="是否保存为草稿，保存后您需签署后才能提交"
                onCancel={() => setSaveConfirmOpen(false)}
                onSubmit={saveDraft}
            />
        </section>
    );
}

/** 提货详情逐项对应旧 detail/index.vue，并复用统一详情与文件组件。 */
export function PurchaseGoodsDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const purGoodsType = params.get("purGoodsType") as PurchaseGoodsActive | null;
    const user = useUserSession();
    const account = useAccountSession();
    const [info, setInfo] = useState<PurchaseGoodsVo>();
    const [confirmState, setConfirmState] = useState<{ title: string; content: string; completed?: string; action: () => Promise<unknown> }>();
    const load = async () => {
        if (id) setInfo(await purchaseGoodsService.getInfo(id));
    };
    useEffect(() => {
        void load();
    }, [id]);
    if (!info) return <section className="purchase-goods-detail-page detail-container" />;
    const goods = info.purchaseGoods;
    const isMy = purGoodsType === "My";
    const isSupplyChain = user.roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const hasPermission =
        account.accountType !== AccountType.SUB_ACCOUNT ||
        Boolean((user.userInfo as any).permissions?.includes(PERMISSION.FREIGHT.code));
    const sealUser = String(account.actualAccount?.id) === String((goods.receiverEnterprise as any)?.signId);
    const enterpriseName = (value: any) => value?.name || value?.legalRepresentative || value?.realName || "--";
    const productType = [
        info.project?.productTypeName,
        (info.project as any)?.area,
        (info.project as any)?.productTypeId
    ]
        .filter(Boolean)
        .join(" | ");
    const receiptState =
        goods.state === PURCHASE_GOODS_STATE.FINISHED.code ? RECEIPT_TYPE.getIntroByCode(goods.receiptState) : "--";
    /** 签署类动作沿用旧版票据跳转与无票据提示。 */
    const openTicket = async (action: () => Promise<any>) => {
        const data = await action();
        if (data?.errors || data?.status === 400) return;
        if (!data?.ticket) {
            void message.success("已发起签署，请等待专人进行签署");
            return;
        }
        window.open(data.ticket);
        await load();
    };
    /** 详情操作继续使用旧 confirmWrapper 对应的消息框。 */
    const confirm = (content: string, action: () => Promise<unknown>, completed?: string, title = "提示") => {
        setConfirmState({ title, content, completed, action });
    };
    const actions: DetailAction<PurchaseGoods>[] = [
        {
            label: "修改",
            visible: row =>
                isMy &&
                user.roleCode === ROLE_CODE.CUSTOMER.code &&
                hasPermission &&
                row.state === PURCHASE_GOODS_STATE.DRAFT.code,
            action: row => navigate(`/console/purchaseGoods/add?id=${row.id}`)
        },
        {
            label: "发起签署",
            visible: row => isMy && hasPermission && row.state === PURCHASE_GOODS_STATE.DRAFT.code,
            action: row => openTicket(() => purchaseGoodsService.startSign(row.id))
        },
        {
            label: "签署",
            visible: row => isMy && row.state === PURCHASE_GOODS_STATE.SIGNING.code && sealUser,
            action: row => openTicket(() => purchaseGoodsService.sign(row.id))
        },
        {
            label: "删除",
            visible: row => isMy && hasPermission && row.state === PURCHASE_GOODS_STATE.DRAFT.code,
            action: row =>
                confirm("是否删除，删除后您可以重新发起提货", async () => {
                    await purchaseGoodsService.delete(row.id);
                    navigate("/console/purchaseGoods");
                }, "已删除")
        },
        {
            label: "发起作废",
            visible: row =>
                isMy &&
                hasPermission &&
                row.state === PURCHASE_GOODS_STATE.FINISHED.code &&
                row.receiptState === RECEIPT_TYPE.UNSIGNED.code &&
                row.forward === FORWARD.UNFORWARD.code,
            action: row =>
                confirm(
                    "作废后，需签署作废提货单后，该提货单将作废，是否发起作废？",
                    () => openTicket(() => purchaseGoodsService.startInvalid(Number(row.id))),
                    "已发起作废"
                )
        },
        {
            label: "签署作废文件",
            visible: row => isMy && row.state === PURCHASE_GOODS_STATE.INVALIDING.code && sealUser,
            action: row => openTicket(() => purchaseGoodsService.sign(row.id))
        },
        { label: "返回", action: () => navigate("/console/purchaseGoods") }
    ];
    return (
        <section className="purchase-goods-detail-page detail-container">
            <DetailHeader
                title={`${isMy ? "我的" : "下游"}提货单详情`}
                leftInfos={[{ label: "编号", value: goods.id }]}
                rightInfos={[{ label: "更新时间", value: dateTimeFormat(goods.updatedTime) }]}
                state={isMy ? { text: PURCHASE_GOODS_STATE.getIntroByCode(goods.state) ?? undefined } : undefined}
                menu={
                    <Breadcrumb
                        routes={[
                            { name: "采购" },
                            { name: "提货单", path: "/console/purchaseGoods" },
                            { name: "提货单详情" }
                        ]}
                    />
                }
            />
            <InfoCard title="基本信息">
                <ZhGrid cols={5}>
                    <ZhBaseItem label="项目" span={2}>
                        <Button
                            className="purchase-goods-project-link"
                            type="link"
                            onClick={() => navigate(`/console/project/detail?id=${goods.projectId}`)}
                        >
                            {goods.projectName || "--"}
                        </Button>
                    </ZhBaseItem>
                    <ZhBaseItem label="品类" span={3} value={productType || goods.productTypeName || "--"} />
                    <ZhBaseItem label="供货单位" value={goods.supplierEnterprise?.name || "--"} />
                    <ZhBaseItem label="提货单位" value={enterpriseName(goods.receiverEnterprise)} />
                    <ZhBaseItem label="提货申请日期" value={dateFormat(goods.applyDate)} />
                    <ZhBaseItem label="预计提货日期" value={dateFormat(goods.expectedDeliveryDate)} />
                    <ZhBaseItem label="吨数" value={formatNumber(goods.quantity)} />
                    <ZhBaseItem label="预估单价" value={`${formatNumber(goods.estimatedUnitPrice, 2)} 元/吨`} />
                    <ZhBaseItem label="单据" fileId={info.file?.id} />{" "}
                    <ZhBaseItem label="签收状态" value={receiptState} />
                    {goods.receiptState === RECEIPT_TYPE.FINISHED.code ? (
                        <ZhBaseItem label="关联签收单">
                            <Button
                                className="purchase-goods-project-link"
                                type="link"
                                onClick={() =>
                                    navigate(
                                        `/console/purchaseGoodsReceipt/detail?id=${goods.goodsReceiptId}&purGoodsReceiptType=${isMy ? "Up" : "Down"}`
                                    )
                                }
                            >
                                {goods.goodsReceiptId || "--"}
                            </Button>
                        </ZhBaseItem>
                    ) : null}
                    <ZhBaseItem label="备注" span={5} value={goods.remark || "--"} />
                </ZhGrid>
            </InfoCard>
            <InfoCard title="关联船舶">
                <div className="purchase-goods-detail-ships">
                    {goods.ships.map((ship, index) => (
                        <button
                            className="purchase-goods-readonly-ship"
                            key={`${ship.id}-${index}`}
                            type="button"
                            onClick={() => navigate(`/logistics/findLocation?mmsi=${ship.id}`)}
                        >
                            <strong>{[ship.cnName, ship.name].filter(Boolean).join("/") || "--"}</strong>
                            {isMy && isSupplyChain ? (
                                <span>
                                    <i>货主</i>
                                    {ship.buyerName || "--"}
                                </span>
                            ) : null}
                            <span>
                                <i>船运单</i>
                                {ship.orderShipId || "--"}
                            </span>
                            <span>
                                <i>提货吨数</i>
                                {formatNumber(ship.ton)}
                            </span>
                            <span>
                                <i>手机号</i>
                                {ship.captinMobile || "--"}
                            </span>
                            <span>
                                <i>备注</i>
                                {ship.remark || "--"}
                            </span>
                        </button>
                    ))}
                </div>
            </InfoCard>
            {goods.state === PURCHASE_GOODS_STATE.INVALIDING.code ||
            goods.state === PURCHASE_GOODS_STATE.INVALID.code ? (
                <InfoCard title="作废信息">
                    <ZhGrid cols={5} gap={16}>
                        <ZhBaseItem label="文件" fileId={goods.invalidFileId} />
                        {goods.state === PURCHASE_GOODS_STATE.INVALID.code ? (
                            <ZhBaseItem label="时间" value={dateTimeFormat(goods.invalidTime)} />
                        ) : null}
                    </ZhGrid>
                </InfoCard>
            ) : null}
            <DetailButtons actions={actions} entity={goods} />
            <ZhMessageBox
                open={Boolean(confirmState)}
                title={confirmState?.title}
                tip={confirmState?.content}
                onCancel={() => setConfirmState(undefined)}
                onSubmit={async () => {
                    if (!confirmState) return;
                    await confirmState.action();
                    if (confirmState.completed) void message.success(confirmState.completed);
                    setConfirmState(undefined);
                }}
            />
        </section>
    );
}

/** 我的商品列表只允许已上架商品进入公开详情。 */
export function ConsoleProductPage() {
    const navigate = useNavigate();
    const supplierId = useUserSession(state => state.userInfo.customer.id);
    const priceTypes = metaOptions(PRODUCT_PRICE_TYPE);
    const states = metaOptions(PRODUCT_STATE);
    const columns: ProColumn<ProductVo>[] = [
        {
            key: "code",
            title: "编号",
            width: 180,
            render: (_, row) => PRODUCT_STATE.UP_SHELF.match(row.product.state) ? (
                <Button className="console-product-code" type="link" onClick={() => navigate(`/public/trade/product/${row.product.id}`)}>
                    {row.product.code}
                </Button>
            ) : <span>{row.product.code}</span>
        },
        {
            key: "mainFile",
            title: "主图",
            width: 80,
            render: (_, row) =>
                row.mainFile?.path ? (
                    <Image className="console-product-main-file" width={40} height={40} preview={false} src={row.mainFile.path} alt={row.product.name} />
                ) : (
                    "--"
                )
        },
        {
            key: "name",
            title: "标题",
            minWidth: 198,
            search: { key: "keyword", type: "input", placeholder: "请输入商品名称", span: 4, order: 1 },
            render: (_, row) => row.product.name
        },
        {
            key: "productTypeName",
            title: "品类",
            minWidth: 198,
            render: (_, row) =>
                [row.productType?.name, row.productType?.area, row.productType?.id].filter(Boolean).join(" | ") || "--"
        },
        { key: "spec", title: "规格", width: 142, render: (_, row) => row.product.productTypeSpec },
        {
            key: "priceType",
            title: "价格类型",
            width: 142,
            enum: priceTypes,
            render: (_, row) => PRODUCT_PRICE_TYPE.getIntroByCode(row.product.priceType)
        },
        { key: "unitPrice", title: "单价(元/吨)", width: 114, render: (_, row) => formatNumber(row.product.unitPrice, 2) },
        {
            key: "updatedTime",
            title: "更新时间",
            width: 170,
            render: (_, row) => dateTimeFormat(row.product.updatedTime)
        },
        {
            key: "state",
            title: "状态",
            width: 86,
            enum: states,
            search: { key: "state", type: "select", placeholder: "请选择状态", options: states, span: 2, order: 2 },
            render: (_, row) => PRODUCT_STATE.getIntroByCode(row.product.state)
        }
    ];
    return (
        <section className="console-product-page section-card">
            <Breadcrumb routes={[{ name: "采购" }, { name: "我的商品列表" }]} />
            <div className="console-product-table">
                <ProTable
                    title="我的商品列表"
                    rowKey={row => row.product.id}
                    columns={columns}
                    request={params => productService.productPage({ ...params, supplierId, isCustomPC: 1 })}
                />
            </div>
        </section>
    );
}
